import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { EASE } from "./Shared";
import BundleMockup from "./BundleMockup";
import { ArrowUpRight } from "lucide-react";
import { CHECKOUT_URL } from "../config";

const MaskLine = ({ children, delay = 0, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block will-change-transform"
      initial={{ y: "115%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const ghostY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const mockY = useTransform(scrollYProgress, [0, 1], [0, 60]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[100svh] flex items-center overflow-hidden pt-20 pb-16 sm:pt-28"
    >
      <motion.span
        style={{ y: ghostY }}
        aria-hidden="true"
        className="pointer-events-none select-none absolute -bottom-8 -left-4 font-display font-extrabold uppercase leading-none text-[26vw] text-outline whitespace-nowrap"
      >
        Blueprint
      </motion.span>

      <div className="relative z-10 w-full max-w-6xl mx-auto px-4 sm:px-8 grid lg:grid-cols-12 gap-14 lg:gap-8 items-center">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
            data-testid="hero-eyebrow"
            className="font-mono text-[10px] sm:text-[11px] tracking-[0.3em] uppercase text-lime mb-5 max-w-md leading-relaxed"
          >
            For entrepreneurs ready to become the person their vision requires
          </motion.p>

          <h1 className="font-display font-extrabold uppercase leading-[0.92] text-[13vw] sm:text-6xl lg:text-7xl">
            <MaskLine delay={0.15}>Your next level</MaskLine>
            <MaskLine delay={0.25}>doesn&rsquo;t need</MaskLine>
            <MaskLine delay={0.35}>more hustle.</MaskLine>
            <MaskLine delay={0.5} className="mt-3 text-fog text-[6vw] sm:text-3xl lg:text-4xl">
              It needs a
            </MaskLine>
            <MaskLine delay={0.62}>
              <span className="font-serif italic normal-case font-medium text-lime tracking-normal">
                Blueprint.
              </span>
            </MaskLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8, ease: EASE }}
            className="mt-5 max-w-md text-sm sm:text-base leading-relaxed text-fog"
          >
            Your beliefs. Your identity. Your relationship with money. Your message. Your
            offer.{" "}
            <span className="text-cream">
              Bring the pieces together — and build from the person you are becoming
            </span>{" "}
            instead of repeating the version you have already outgrown.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9, ease: EASE }}
            className="mt-5 inline-flex flex-wrap gap-px bg-cream/10 border border-cream/10"
            data-testid="hero-offer-line"
          >
            {["The complete Blueprint course", "+ Skool access", "+ Bonus: Becoming HER book"].map(
              (t) => (
                <span
                  key={t}
                  className="bg-ink px-4 py-2.5 font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.18em] text-cream"
                >
                  {t}
                </span>
              ),
            )}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1, ease: EASE }}
            className="mt-6 flex flex-wrap items-end gap-x-7 gap-y-5"
          >
            <div data-testid="hero-price">
              <span className="font-display font-extrabold text-5xl sm:text-6xl leading-none">
                $111
              </span>
              <span className="block font-mono text-[10px] tracking-[0.25em] uppercase text-fog mt-2">
                One-time payment
              </span>
            </div>
            <motion.a
              href={CHECKOUT_URL}
              data-testid="hero-cta-button"
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              transition={{ duration: 0.25, ease: EASE }}
              className="group inline-flex items-center gap-2 bg-lime text-ink font-display font-bold uppercase tracking-wide text-sm sm:text-base px-7 py-4 transition-colors duration-300 hover:bg-cream shadow-[0_0_50px_rgba(204,255,0,0.15)]"
            >
              Build my blueprint — $111
              <ArrowUpRight
                size={18}
                strokeWidth={2.5}
                className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
              />
            </motion.a>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.15 }}
            className="mt-4 font-mono text-[10px] tracking-[0.22em] uppercase text-fog"
          >
            One payment · Instant digital access
          </motion.p>
        </div>

        <motion.div
          style={{ y: mockY }}
          initial={{ opacity: 0, scale: 0.94, y: 30 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 1.1, delay: 0.65, ease: EASE }}
          className="lg:col-span-5"
          data-testid="hero-bundle-mockup"
        >
          <BundleMockup />
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 hidden sm:flex flex-col items-center gap-2"
        aria-hidden="true"
      >
        <span className="font-mono text-[9px] tracking-[0.35em] uppercase text-fog">Scroll</span>
        <motion.span
          animate={{ scaleY: [0, 1, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
          className="w-px h-8 bg-lime origin-top"
        />
      </motion.div>
    </section>
  );
}
